import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { AuthCard, AuthLabel, authLinkClass } from '@/components/auth/AuthCard'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')
  
  const { resetPassword, isLoading } = useAuthStore()
  const { toast } = useToast()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    if (!email) {
      setError('Email is required')
      return
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address')
      return
    }
    
    const result = await resetPassword(email)
    
    if (result.success) {
      setIsSubmitted(true)
      toast({
        title: "Reset link sent!",
        description: "Check your email for password reset instructions.",
        variant: "success",
      })
    } else {
      setError(result.error || 'Failed to send reset email. Please try again.')
    }
  }

  const backToSignIn = (
    <p className="mt-5 text-center text-sm">
      <Link to="/auth/login" className={authLinkClass}>
        <span aria-hidden="true">← </span>Back to sign in
      </Link>
    </p>
  )

  if (isSubmitted) {
    return (
      <div>
        <AuthCard
          title="Check your email"
          subtitle={
            <>
              We sent a reset link to <span className="text-slate-900">{email}</span>. If it doesn&apos;t arrive in a few minutes, check your spam folder.
            </>
          }
        >
          <Button variant="outline" size="lg" className="w-full" onClick={() => setIsSubmitted(false)}>
            Use a different email
          </Button>
          <p className="mt-5 text-[13px] text-slate-500">
            Still stuck?{' '}
            <a href="mailto:support@healthycoach.io" className={authLinkClass}>Contact support</a>
          </p>
        </AuthCard>
        {backToSignIn}
      </div>
    )
  }

  return (
    <div>
      <AuthCard title="Reset your password" subtitle="We'll email you a link. It expires in 24 hours.">
        <form onSubmit={handleSubmit} noValidate>
          <AuthLabel htmlFor="email">Email</AuthLabel>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setError('')
            }}
            className={`h-12 ${error ? 'border-red-300 focus-visible:border-red-500 focus-visible:ring-red-500/15' : ''}`}
            placeholder="you@company.com"
            aria-invalid={Boolean(error)}
          />
          {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}

          <Button type="submit" size="lg" className="mt-3.5 w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending reset link...
              </>
            ) : (
              'Send reset link'
            )}
          </Button>
        </form>
      </AuthCard>
      {backToSignIn}
    </div>
  )
}

export default ForgotPasswordPage
